import {
  BidStatus,
  NegotiationRoundStatus,
  ProcedureType,
  type NegotiationRoundDto,
  type ReceivedBidDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { isFoaPartII, isFoaPartIII } from "../../sourcing-events/buyer/wizard/utils/foaPartII";

export const allowsPartIIDialogue = (event: SourcingEventDto): boolean =>
  event.eventContext.type === "Public" && isFoaPartII(event.eventContext.regulation, event.eventContext.foaPart);

export const dialoguePlanned = (event: SourcingEventDto): boolean =>
  event.eventContext.type === "Public" && event.eventContext.dialoguePlanned === true;

export const holdsDialogue = (rounds: readonly NegotiationRoundDto[]): boolean =>
  rounds.some((round) => round.status !== NegotiationRoundStatus.Draft);

export const negotiatesUnderFoaPartIII = (event: SourcingEventDto): boolean =>
  event.eventContext.type === "Public" &&
  event.eventContext.procedureType === ProcedureType.NegotiatedWithPriorCall &&
  isFoaPartIII(event.eventContext.regulation, event.eventContext.foaPart);

export const reservedAwardOnInitialTenders = (event: SourcingEventDto): boolean =>
  event.eventContext.type === "Public" && event.eventContext.noNegotiationNecessary === true;

// Mirrors SourcingEvent.mustNegotiateBeforeAward in the BFF: FOA § 23-7 (5).
export const mustNegotiateBeforeAward = (event: SourcingEventDto, rounds: readonly NegotiationRoundDto[]): boolean =>
  negotiatesUnderFoaPartIII(event) && !reservedAwardOnInitialTenders(event) && !holdsDialogue(rounds);

export const draftRoundOf = (rounds: readonly NegotiationRoundDto[]): NegotiationRoundDto | undefined =>
  rounds.find((round) => round.status === NegotiationRoundStatus.Draft);

export const startingDialogueDepartsFromPlan = (
  event: SourcingEventDto,
  rounds: readonly NegotiationRoundDto[],
): boolean => allowsPartIIDialogue(event) && !dialoguePlanned(event) && rounds.length === 0;

export const awardingDepartsFromPlan = (event: SourcingEventDto, rounds: readonly NegotiationRoundDto[]): boolean =>
  allowsPartIIDialogue(event) && dialoguePlanned(event) && !holdsDialogue(rounds);

// Mirrors tendersAtStage in the BFF: FOA § 9-3 (2) makes leaving a Part II tenderer out of the
// dialogue no reduction, so each tenderer competes on its latest tender up to the stage.
export const standingTendersAtStage = (
  bids: readonly ReceivedBidDto[],
  rounds: readonly NegotiationRoundDto[],
  stage: NegotiationRoundDto,
): ReceivedBidDto[] => {
  const roundNumbers = new Map(
    rounds.filter((round) => round.status !== NegotiationRoundStatus.Draft).map((round) => [round.id, round.roundNumber]),
  );
  const stageOf = (bid: ReceivedBidDto) =>
    bid.forNegotiationRound == null ? 0 : roundNumbers.get(bid.forNegotiationRound) ?? Number.POSITIVE_INFINITY;
  const later = (a: ReceivedBidDto, b: ReceivedBidDto) =>
    stageOf(a) > stageOf(b) || (stageOf(a) === stageOf(b) && (a.submittedAt ?? "") > (b.submittedAt ?? ""));
  const latest = new Map<string, ReceivedBidDto>();
  for (const bid of bids) {
    if (bid.status === BidStatus.Draft || stageOf(bid) > stage.roundNumber) continue;
    const current = latest.get(bid.owner);
    if (!current || later(bid, current)) latest.set(bid.owner, bid);
  }
  return [...latest.values()];
};
